import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang } from '../i18n/LanguageContext';
import { CUSTOMER_PORTAL_URL } from '../config';

const REGIONS = ['US', 'UK', 'Middle East', 'APAC', 'Other'];
const ORG_TYPES = ['', 'Production', 'Sandbox', 'Developer Edition', 'Scratch Org'];

const inputCls = "w-full rounded-xl border border-[#10282C]/12 bg-white px-4 py-3 text-[14px] text-[#10282C] outline-none focus:border-[#00918F] placeholder:text-[#10282C]/30";

export default function SignUp() {
  const { t } = useLang();
  const [form, setForm] = useState({ full_name: '', email: '', company: '', country: '', sf_org_type: '' });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // No backend on this site — hand off to the customer portal, prefilled
    // with whatever the visitor already entered here.
    const params = new URLSearchParams({
      name: form.full_name,
      email: form.email,
      company: form.company,
      country: form.country,
      sf_org_type: form.sf_org_type,
    });
    window.location.href = `${CUSTOMER_PORTAL_URL}/signup?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-[#F6F8F7]">
      <section className="bg-[#10282C] grain relative overflow-hidden pt-32 pb-40">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <h1 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            {t('auth.signupTitle')}
          </h1>
          <p className="mt-4 text-[15px] text-white/55 leading-relaxed max-w-lg mx-auto">
            {t('auth.signupSub')}
          </p>
        </div>
      </section>

      <div className="max-w-xl mx-auto px-5 sm:px-8 -mt-24 relative z-20 pb-28">
        <div className="rounded-3xl bg-white border border-[#10282C]/8 shadow-[0_32px_80px_-32px_rgba(16,40,44,0.3)] p-8 sm:p-10">
          <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="signup-form"
          >
            <div>
              <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('auth.fullName')} *</label>
              <input data-testid="signup-input-name" required value={form.full_name} onChange={set('full_name')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('auth.email')} *</label>
              <input data-testid="signup-input-email" required type="email" value={form.email} onChange={set('email')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
            </div>
            <div>
              <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('auth.company')} *</label>
              <input data-testid="signup-input-company" required value={form.company} onChange={set('company')} className={`mt-1.5 ${inputCls}`} style={{ transition: 'border-color 0.2s ease' }} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('auth.country')} *</label>
                <select data-testid="signup-select-country" required value={form.country} onChange={set('country')} className={`mt-1.5 ${inputCls}`}>
                  <option value="" disabled>{t('common.selectPlaceholder')}</option>
                  {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12.5px] font-semibold text-[#10282C]/70">{t('auth.sfOrg')}</label>
                <select data-testid="signup-select-orgtype" value={form.sf_org_type} onChange={set('sf_org_type')} className={`mt-1.5 ${inputCls}`}>
                  {ORG_TYPES.map((o) => <option key={o} value={o}>{o || t('common.selectPlaceholder')}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="signup-submit-btn" type="submit" className="btn-coral w-full py-3.5 rounded-full text-[15px] font-semibold">
              {t('auth.createAccount')}
            </button>
            <p className="text-center text-[13px] text-[#10282C]/50">
              {t('auth.haveAccount')}{' '}
              <Link to="/login" data-testid="signup-login-link" className="text-[#00918F] font-semibold">{t('auth.signIn')}</Link>
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}
